import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import {
  DC_COLLECTION_TYPES,
  DcRosterConfigDocument,
  DcRosterConfigEvent
} from '../../domain/dc-roster-config.model';
import { AlertService } from '../../services/alert.service';
import { DcRosterService } from '../../services/dc-roster.service';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { DcRosterConfigDialogComponent } from './dc-roster-config-dialog/dc-roster-config-dialog.component';
import { DcRosterConfigEventDialogComponent } from './dc-roster-config-event-dialog/dc-roster-config-event-dialog.component';

@Component({
  selector: 'app-dc-roster-config',
  templateUrl: './dc-roster-config.component.html',
  styleUrls: ['./dc-roster-config.component.css']
})
export class DcRosterConfigComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'expand',
    'dcType',
    'description',
    'subType',
    'codes',
    'actions'
  ];
  readonly detailRowColumns = ['expandedDetail'];
  readonly eventDisplayedColumns = [
    'code',
    'collectionType',
    'effort',
    'eventGrade',
    'actions'
  ];
  readonly collectionTypes = DC_COLLECTION_TYPES;

  filteredConfigs: DcRosterConfigDocument[] = [];
  dcTypeOptions: string[] = [];
  subTypeOptions: string[] = [];
  filteredConfigCount = 0;
  filteredCodeCount = 0;
  loading = true;
  saving = false;
  hasWritePermission = false;
  expandedConfigId: string | null = null;

  searchInputValue = '';

  private configsValue: DcRosterConfigDocument[] = [];
  private searchTermValue = '';
  private dcTypeFilterValue = '';
  private subTypeFilterValue = '';
  private collectionTypeFilterValue = '';

  private readonly searchChange$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dcRosterService: DcRosterService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    this.searchChange$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
      });

    this.hasWritePermission = await firstValueFrom(
      this.authService.hasPermission('write:dc-roster')
    );
    await this.loadConfigs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchChange$.complete();
  }

  get configs(): DcRosterConfigDocument[] {
    return this.configsValue;
  }

  set configs(configs: DcRosterConfigDocument[]) {
    this.configsValue = configs;
    this.applyFilters();
  }

  get searchTerm(): string {
    return this.searchTermValue;
  }

  set searchTerm(searchTerm: string) {
    this.searchTermValue = searchTerm;
    this.applyFilters();
  }

  get dcTypeFilter(): string {
    return this.dcTypeFilterValue;
  }

  set dcTypeFilter(dcTypeFilter: string) {
    this.dcTypeFilterValue = dcTypeFilter;
    this.applyFilters();
  }

  get subTypeFilter(): string {
    return this.subTypeFilterValue;
  }

  set subTypeFilter(subTypeFilter: string) {
    this.subTypeFilterValue = subTypeFilter;
    this.applyFilters();
  }

  get collectionTypeFilter(): string {
    return this.collectionTypeFilterValue;
  }

  set collectionTypeFilter(collectionTypeFilter: string) {
    this.collectionTypeFilterValue = collectionTypeFilter;
    this.applyFilters();
  }

  /**
   * Recomputed only when the configs or the filters change. Binding a getter
   * straight to `[dataSource]` would hand MatTable a new array on every change
   * detection pass and force a full re-render each time.
   */
  private applyFilters(): void {
    this.dcTypeOptions = this.uniqueSortedValues(
      this.configs.map((config) => config.dcType)
    );
    this.subTypeOptions = this.uniqueSortedValues(
      this.configs.map((config) => config.subType)
    );
    this.filteredConfigs = this.buildFilteredConfigs();
    this.filteredConfigCount = this.filteredConfigs.length;
    this.filteredCodeCount = this.filteredConfigs.reduce(
      (count, config) => count + config.collectionEvents.length,
      0
    );
  }

  private buildFilteredConfigs(): DcRosterConfigDocument[] {
    const searchTerm = this.searchTerm.trim().toLowerCase();

    return this.configs
      .map((config) => {
        if (
          (this.dcTypeFilter && config.dcType !== this.dcTypeFilter) ||
          (this.subTypeFilter && config.subType !== this.subTypeFilter)
        ) {
          return null;
        }

        const collectionEvents = config.collectionEvents.filter(
          (event) =>
            !this.collectionTypeFilter ||
            event.collectionType === this.collectionTypeFilter
        );
        if (this.collectionTypeFilter && collectionEvents.length === 0) {
          return null;
        }
        if (!searchTerm) {
          return { ...config, collectionEvents };
        }

        const groupMatches = [
          config.dcType,
          config.subType,
          config.description
        ].some((value) => value.toLowerCase().includes(searchTerm));
        if (groupMatches) {
          return { ...config, collectionEvents };
        }

        const matchingEvents = collectionEvents.filter((event) =>
          event.code.toLowerCase().includes(searchTerm)
        );
        return matchingEvents.length > 0
          ? { ...config, collectionEvents: matchingEvents }
          : null;
      })
      .filter((config): config is DcRosterConfigDocument => config !== null);
  }

  onSearchChanged(searchTerm: string): void {
    this.searchInputValue = searchTerm;
    this.searchChange$.next(searchTerm);
  }

  clearFilters(): void {
    this.searchInputValue = '';
    this.searchChange$.next('');
    this.searchTermValue = '';
    this.dcTypeFilterValue = '';
    this.subTypeFilterValue = '';
    this.collectionTypeFilterValue = '';
    this.applyFilters();
  }

  toggleExpanded(config: DcRosterConfigDocument): void {
    this.expandedConfigId =
      this.expandedConfigId === config._id ? null : config._id;
  }

  isExpanded(config: DcRosterConfigDocument): boolean {
    return this.expandedConfigId === config._id;
  }

  async openAddConfig(): Promise<void> {
    if (!this.hasWritePermission || this.saving) {
      return;
    }
    const dialogRef = this.dialog.open(DcRosterConfigDialogComponent, {
      width: '480px',
      data: { mode: 'create' }
    });
    const value = await firstValueFrom(dialogRef.afterClosed());
    if (!value) {
      return;
    }

    try {
      this.saving = true;
      const created = await firstValueFrom(
        this.dcRosterService.createAdminConfig({
          ...value,
          collectionEvents: []
        })
      );
      this.setConfigs([...this.configs, created]);
      this.expandedConfigId = created._id;
      this.alertService.showInfo('Configuration created.');
    } catch (error) {
      this.showMutationError('create configuration', error);
    } finally {
      this.saving = false;
    }
  }

  async openEditConfig(config: DcRosterConfigDocument): Promise<void> {
    if (!this.hasWritePermission || this.saving) {
      return;
    }
    const dialogRef = this.dialog.open(DcRosterConfigDialogComponent, {
      width: '480px',
      data: { mode: 'edit', config }
    });
    const value = await firstValueFrom(dialogRef.afterClosed());
    if (!value) {
      return;
    }

    try {
      this.saving = true;
      const updated = await firstValueFrom(
        this.dcRosterService.updateAdminConfig(config._id, value)
      );
      this.patchConfig(updated);
      this.alertService.showInfo('Configuration updated.');
    } catch (error) {
      this.showMutationError('update configuration', error);
    } finally {
      this.saving = false;
    }
  }

  async confirmDeleteConfig(config: DcRosterConfigDocument): Promise<void> {
    if (!this.hasWritePermission || this.saving) {
      return;
    }

    const fullConfig =
      this.configs.find(
        (existingConfig) => existingConfig._id === config._id
      ) ?? config;

    let rosterCount: number;
    try {
      rosterCount = await firstValueFrom(
        this.dcRosterService.countRostersUsingConfig(fullConfig._id)
      );
    } catch (error) {
      this.showMutationError('check configuration usage', error);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '480px',
      data: {
        title: 'Delete configuration',
        message:
          `Delete ${fullConfig.dcType} / ${fullConfig.subType} and its ` +
          `${fullConfig.collectionEvents.length} collection code(s)? ` +
          `Used by ${rosterCount} scheduled DC roster(s).`,
        okButtonCaption: 'Delete'
      }
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    try {
      this.saving = true;
      await firstValueFrom(this.dcRosterService.deleteAdminConfig(config._id));
      this.setConfigs(
        this.configs.filter(
          (existingConfig) => existingConfig._id !== config._id
        )
      );
      if (this.expandedConfigId === config._id) {
        this.expandedConfigId = null;
      }
      this.alertService.showInfo('Configuration deleted.');
    } catch (error) {
      this.showMutationError('delete configuration', error);
    } finally {
      this.saving = false;
    }
  }

  async openAddEvent(config: DcRosterConfigDocument): Promise<void> {
    if (!this.hasWritePermission || this.saving) {
      return;
    }
    const dialogRef = this.dialog.open(DcRosterConfigEventDialogComponent, {
      width: '500px',
      data: { mode: 'create' }
    });
    const event = await firstValueFrom(dialogRef.afterClosed());
    if (!event) {
      return;
    }

    try {
      this.saving = true;
      const updated = await firstValueFrom(
        this.dcRosterService.addAdminCollectionEvent(config._id, event)
      );
      this.patchConfig(updated);
      this.alertService.showInfo('Collection code added.');
    } catch (error) {
      this.showMutationError('add collection code', error);
    } finally {
      this.saving = false;
    }
  }

  async openEditEvent(
    config: DcRosterConfigDocument,
    event: DcRosterConfigEvent
  ): Promise<void> {
    if (!this.hasWritePermission || this.saving) {
      return;
    }
    const originalCode = event.code;
    const dialogRef = this.dialog.open(DcRosterConfigEventDialogComponent, {
      width: '500px',
      data: { mode: 'edit', event }
    });
    const value = await firstValueFrom(dialogRef.afterClosed());
    if (!value) {
      return;
    }

    try {
      this.saving = true;
      const updated = await firstValueFrom(
        this.dcRosterService.updateAdminCollectionEvent(
          config._id,
          originalCode,
          {
            collectionType: value.collectionType,
            effort: value.effort,
            eventGrade: value.eventGrade
          }
        )
      );
      this.patchConfig(updated);
      this.alertService.showInfo('Collection code updated.');
    } catch (error) {
      this.showMutationError('update collection code', error);
    } finally {
      this.saving = false;
    }
  }

  async confirmDeleteEvent(
    config: DcRosterConfigDocument,
    event: DcRosterConfigEvent
  ): Promise<void> {
    if (!this.hasWritePermission || this.saving || event.reserved) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '440px',
      data: {
        title: 'Delete collection code',
        message: `Delete collection code ${event.code} from ${config.dcType} / ${config.subType}?`,
        okButtonCaption: 'Delete'
      }
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    try {
      this.saving = true;
      const updated = await firstValueFrom(
        this.dcRosterService.deleteAdminCollectionEvent(config._id, event.code)
      );
      this.patchConfig(updated);
      this.alertService.showInfo('Collection code deleted.');
    } catch (error) {
      this.showMutationError('delete collection code', error);
    } finally {
      this.saving = false;
    }
  }

  trackByConfigId(_index: number, config: DcRosterConfigDocument): string {
    return config._id;
  }

  trackByEventCode(_index: number, event: DcRosterConfigEvent): string {
    return event.code;
  }

  private async loadConfigs(): Promise<void> {
    try {
      this.loading = true;
      const configs = await firstValueFrom(
        this.dcRosterService.getAdminConfigs()
      );
      this.setConfigs(configs);
    } catch (error) {
      this.alertService.showError(
        'Could not load DC roster configurations: ' +
          this.getErrorMessage(error)
      );
    } finally {
      this.loading = false;
    }
  }

  private patchConfig(updated: DcRosterConfigDocument): void {
    this.setConfigs(
      this.configs.map((config) =>
        config._id === updated._id ? updated : config
      )
    );
  }

  private setConfigs(configs: DcRosterConfigDocument[]): void {
    this.configs = this.sortConfigs(configs);
  }

  private sortConfigs(
    configs: DcRosterConfigDocument[]
  ): DcRosterConfigDocument[] {
    return [...configs].sort(
      (left, right) =>
        left.dcType.localeCompare(right.dcType) ||
        left.subType.localeCompare(right.subType)
    );
  }

  private uniqueSortedValues(values: string[]): string[] {
    return [...new Set(values.filter((value) => !!value))].sort((left, right) =>
      left.localeCompare(right)
    );
  }

  private showMutationError(action: string, error: unknown): void {
    this.alertService.showError(
      `Could not ${action}: ${this.getErrorMessage(error)}`
    );
  }

  private getErrorMessage(error: any): string {
    const validationErrors = Array.isArray(error?.error?.errors)
      ? error.error.errors
          .filter(
            (message: unknown): message is string => typeof message === 'string'
          )
          .map((message: string) => message.trim())
          .filter((message: string) => !!message)
      : [];
    if (validationErrors.length > 0) {
      return validationErrors.join('; ');
    }
    return error?.error?.message ?? error?.message ?? 'Unknown error';
  }
}
